import { appOutOfDate } from '../../../webmodule-common/other/debug';
import { ConvertedQuoteTotalsCache } from '../dashboard/data/converted-quote-totals-cache';
import { customElement, property } from 'lit/decorators.js';
import { dashboardFilterDataCache } from '../dashboard/data/dashboard-filter-data-cache';
import { DashboardFilterOption, DashboardFilterOptions, DateRange } from '../dashboard/widget/dashboard-filter';
import { DeliveryPipelinePoTablePageCache } from '../dashboard/widget/delivery-pipeline-purchaseorder-widget';
import { emptyGuid, newGuid } from '../../../webmodule-common/other/api/guid';
import { getApiFactory } from '../../api/api-injector';
import { getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding';
import { getTerritoryManager, TerritoryContainerManager } from '../../settings/data/territory-manager';
import { TerritoryFranchiseeBranchLink } from '../../api/supplier-api-interface-supplier';
import { html, PropertyValues, TemplateResult } from 'lit';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { PageBase } from './helpers/page-base';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager';
import type { TabPage } from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import { QuoteState } from '../common/object-states';
import { tlang } from '../../../webmodule-common/other/language/lang';
import { userDataStore } from '../common/current-user-data-store';

/**
 * configure the dashboard based on the type of user.
 * we will determine this user level by using a security level on the backend
 * and converting that to a type
 * or maybe add a table that the manager can allocate and configure
 */
const dashboardContentType = {
  unknown: 0,
  /**
   * this is the typical quote creator, and only one implemented
   */
  branchUser: 1,
  franchiseManager: 2,
  franchiseAdministrator: 4
};

@customElement('app-home')
export class AppHome extends PageBase {
  internalId: string = getInternalId();
  // For more information on using properties and state in lit
  // check out this link https://lit.dev/docs/components/properties/
  @property() message = tlang`Welcome!`;
  @property({ type: Number }) contentType = 0;

  private currentDate = new Date();

  private availableDateRanges: DateRange[] = [];
  @property({ type: Object }) private selectedDateRange?: DateRange;
  @property() private selectedTerritoryId?: string;
  @property() private selectedFranchiseeId?: string;
  @property() private selectedBranchId?: string;
  @property() private selectedUserId?: string;
  @property({ type: Object }) private selectedDateRangeForPipeline?: DateRange;
  @property() private selectedTerritoryIdForPipeline?: string;
  @property() private selectedBranchIdForPipeline?: string;
  @property() private selectedUserIdForPipeline?: string;
  @property() private selectedFranchiseeIdForPipeline?: string;

  private convertedQuoteTotalsData = new ConvertedQuoteTotalsCache();

  private deliveryPipelinePoWidgetData = new DeliveryPipelinePoTablePageCache();
  private dashboardFilterCache = dashboardFilterDataCache();

  private territoryManager: TerritoryContainerManager | null = null;
  @property({ type: Array }) private availableTerritories: DashboardFilterOption[] = [];
  @property({ type: Array }) private quoteStates: QuoteState[] = [];
  private territoryLinks: TerritoryFranchiseeBranchLink[] = [];

  private tabManager!: TabManager;

  constructor() {
    super();
    this.contentType = dashboardContentType.branchUser;
    this.tabManager = this.createTabManager();
  }

  private get elementId(): string {
    return `dashboard-page-${this.internalId}`;
  }

  async dispose() {
    //any dashboard data cached would need to be flushed here
    await super.dispose();
  }

  render() {
    if (!this.loggedIn) return html``;
    return html`
      <div id=${this.elementId} class="page-content">
        <div class="page-container">${this.getTemplateForCurrentUser()}</div>
      </div>
    `;
  }

  updated(changedProperties: PropertyValues<this>) {
    super.updated(changedProperties);
    // The tab panels are rendered by the tab manager (a separate element), so a
    // filter change here won't reach the panel widgets on its own. Only re-render
    // the manager when a widget input actually changed: forwarding on every
    // update churns the chart widgets' data fetch.
    const widgetInputs = [
      'selectedDateRange',
      'selectedTerritoryId',
      'selectedFranchiseeId',
      'selectedBranchId',
      'selectedUserId',
      'quoteStates',
      'availableTerritories',
      'contentType',
      'selectedDateRangeForPipeline',
      'selectedTerritoryIdForPipeline',
      'selectedBranchIdForPipeline',
      'selectedUserIdForPipeline',
      'selectedFranchiseeIdForPipeline'
    ];
    if (widgetInputs.some(p => changedProperties.has(p as keyof AppHome))) {
      this.tabManager?.requestUpdate();
    }
  }

  getTemplateForCurrentUser(): TemplateResult {
    return html`${this.tabManager.ui}`;
  }

  private createTabManager(): TabManager {
    const pages: TabPage[] = [
      {
        caption: () => tlang`Overview`,
        fragment: 'overview',
        content: () => this.renderOverviewTab()
      },
      {
        caption: () => tlang`Delivery Pipeline`,
        fragment: 'delivery-pipeline',
        content: () => this.renderDeliveryPipelineTab()
      }
    ];
    return new TabManager({ initialIndex: 0, pages });
  }

  private renderOverviewTab(): TemplateResult {
    const selectedBranchIds = this.getSelectedBranchIds();
    // Defer creating the data widgets until the initial filter inputs are ready,
    // so each renders once cleanly at its final width rather than churning during
    // load (mirrors the dealer dashboard).
    const inputsReady = !!this.selectedDateRange && this.quoteStates.length > 0;
    return html` <div class="dashboard-tab dashboard-tab-overview">
      <wm-dashboard-filter
        class="dashboard-item-filter"
        .dates=${this.availableDateRanges}
        .territories=${this.availableTerritories}
        .franchisees=${this.dashboardFilterCache.getFranchisees()}
        .branches=${this.dashboardFilterCache.getBranchesByFranchiseeId(this.selectedFranchiseeId)}
        .users=${this.dashboardFilterCache.getBranchUsers(this.selectedBranchId)}
        .selectedDate=${this.selectedDateRange}
        .selectedTerritoryId=${this.selectedTerritoryId}
        .selectedFranchiseeId=${this.selectedFranchiseeId}
        .selectedBranchId=${this.selectedBranchId}
        .selectedUserId=${this.selectedUserId}
        @wm-event-date-changed=${this.onDateRangeChanged}
        @wm-event-territory-changed=${this.onTerritoryChanged}
        @wm-event-franchisee-changed=${this.onFranchiseeChanged}
        @wm-event-branch-changed=${this.onBranchChanged}
        @wm-event-user-changed=${this.onUserChanged}
        @wm-event-reset-filters=${this.onFilterReset}
      ></wm-dashboard-filter>
      ${inputsReady
        ? html` <div class="dashboard-overview-body">
            <div class="dashboard-items-wrapper dashboard-kpi-items">
              <div class="dashboard-item dashboard-item-piechart">
                <active-licenses-widget
                  .franchiseeId=${this.selectedFranchiseeId}
                  .branchIds=${selectedBranchIds}
                ></active-licenses-widget>
              </div>
              <div class="dashboard-item dashboard-item-piechart">
                <sales-success-widget
                  .dateRange=${this.selectedDateRange}
                  .branchIds=${selectedBranchIds}
                  .userId=${this.selectedUserId}
                  .cache=${this.convertedQuoteTotalsData}
                ></sales-success-widget>
              </div>
            </div>
            <div class="dashboard-items-wrapper dashboard-chart-items">
              <div class="dashboard-item dashboard-item-barchart">
                <generated-quotes-revenue-widget
                  .dateRange=${this.selectedDateRange}
                  .branchIds=${selectedBranchIds}
                  .userId=${this.selectedUserId}
                  .states=${this.quoteStates}
                ></generated-quotes-revenue-widget>
              </div>
              <div class="dashboard-item dashboard-item-barchart">
                <generated-purchase-orders-revenue-widget
                  .dateRange=${this.selectedDateRange}
                  .branchIds=${selectedBranchIds}
                  .userId=${this.selectedUserId}
                ></generated-purchase-orders-revenue-widget>
              </div>
            </div>
          </div>`
        : html``}
    </div>`;
  }

  private renderDeliveryPipelineTab(): TemplateResult {
    const selectedBranchIds = this.getSelectedBranchIdsForPipeline();
    const inputReady = !!this.selectedDateRangeForPipeline && selectedBranchIds !== undefined;
    return html`<div class="dashboard-tab dashboard-tab-delivery supplier-dashboard-delivery-pipeline">
      <wm-dashboard-filter
        class="dashboard-item-filter"
        .dates=${this.availableDateRanges}
        .territories=${this.availableTerritories}
        .branches=${this.dashboardFilterCache.getBranchesByFranchiseeId(this.selectedFranchiseeIdForPipeline)}
        .users=${this.dashboardFilterCache.getBranchUsers(this.selectedBranchIdForPipeline)}
        .franchisees=${this.dashboardFilterCache.getFranchisees()}
        .selectedDate=${this.selectedDateRangeForPipeline}
        .selectedTerritoryId=${this.selectedTerritoryIdForPipeline}
        .selectedBranchId=${this.selectedBranchIdForPipeline}
        .selectedUserId=${this.selectedUserIdForPipeline}
        .selectedFranchiseeId=${this.selectedFranchiseeIdForPipeline}
        @wm-event-date-changed=${this.onDateRangeChangedForPipeline}
        @wm-event-territory-changed=${this.onTerritoryChangedForPipeline}
        @wm-event-franchisee-changed=${this.onFranchiseeChangedForPipeline}
        @wm-event-branch-changed=${this.onBranchChangedForPipeline}
        @wm-event-user-changed=${this.onUserChangedForPipeline}
        @wm-event-reset-filters=${this.onFilterResetForPipeline}
      ></wm-dashboard-filter>
      ${inputReady
        ? html` <div>
            <delivery-pipeline-po-widget
              .cache=${this.deliveryPipelinePoWidgetData}
              .dateRange=${this.selectedDateRangeForPipeline}
              .branchIds=${selectedBranchIds}
              .assignedToUserId=${this.selectedUserIdForPipeline ?? emptyGuid}
              .territories=${this.territoryManager?.territories}
              .territoryLinks=${this.territoryLinks}
            ></delivery-pipeline-po-widget>
          </div>`
        : html``}
    </div>`;
  }

  meta() {
    return {
      title: `Supplier's Module`,
      titleTemplate: null,
      description: 'SoftTech dealer CPQ'
    };
  }

  protected async awaken() {
    if (appOutOfDate()) {
      return;
    }

    await userDataStore.loadCoreDetails();
    //if we ever reach this page and we are not logged in
    //then we want to force a page reload which will
    //take us through our full reload process of the apoplication
    //in index.html including the user login process
    if (!this.loggedIn) {
      setTimeout(() => (location.href = ''), 100);
    }

    const last6months = new Date(this.currentDate);
    last6months.setMonth(last6months.getMonth() - 6);
    this.addDateRange(tlang`Last 6 months`, last6months);

    const last90days = new Date(this.currentDate);
    last90days.setMonth(last90days.getMonth() - 3);
    this.addDateRange(tlang`Last 90 days`, last90days);

    const last60days = new Date(this.currentDate);
    last60days.setMonth(last60days.getMonth() - 2);
    this.addDateRange(tlang`Last 60 days`, last60days);

    // get start date for activity over the past month
    const last30days = new Date(this.currentDate);
    last30days.setMonth(last30days.getMonth() - 1);
    this.addDateRange(tlang`Last 30 days`, last30days);

    const last7days = new Date(this.currentDate);
    last7days.setDate(last7days.getDate() - 7);
    this.addDateRange(tlang`Last 7 days`, last7days);

    const last24hours = new Date(this.currentDate);
    last24hours.setDate(last24hours.getDate() - 1);
    this.addDateRange(tlang`Last 24 hours`, last24hours);

    userDataStore.requestRefresh();
    await this.dashboardFilterCache.prefetch();

    this.territoryManager = getTerritoryManager();
    await this.territoryManager.needsTerritories();
    this.territoryLinks = this.territoryManager.container.territoryFranchiseBranches;
    const territoryOptions: DashboardFilterOption[] = [];
    for (const territory of this.territoryManager.territories) {
      territoryOptions.push({ id: territory.id, name: territory.name });
    }

    this.selectedDateRange = this.availableDateRanges[3];

    // set properties below here - ensures that all widgets update at once
    this.quoteStates = await this.getQuoteStates();
    this.selectedDateRange = this.availableDateRanges[3];
    this.availableTerritories = territoryOptions;
    this.selectedDateRangeForPipeline = this.availableDateRanges[3];
  }

  protected async afterUserConnected() {
    this.requestUpdate();
  }

  protected addDateRange(label: string, startDate: Date) {
    this.availableDateRanges.push({
      id: newGuid(),
      label: label,
      startDate: startDate,
      endDate: this.currentDate
    });
  }

  protected async getQuoteStates(): Promise<number[]> {
    const quoteStates = [QuoteState.Active, QuoteState.IssuePending, QuoteState.Issued, QuoteState.Accepted];

    const api = getApiFactory().supplier();
    const result = await api.getCoreSupplierSettingsEntry();
    if (result && result.settings.allowSupplierReview) {
      quoteStates.push(
        QuoteState.Accepted_AssignedToReviewer,
        QuoteState.SupplierReviewPending,
        QuoteState.SupplierReviewed
      );
    }
    quoteStates.push(QuoteState.Approved);
    return quoteStates;
  }

  protected getDashboardFilterOptions(): DashboardFilterOptions {
    return {
      dateRange: this.selectedDateRange,
      branchIds: this.getSelectedBranchIds(),
      userId: this.selectedUserId,
      forceEmptyDisplay: false
    };
  }

  /**
   * Gets an array of selected branch ids for a given territory or franchisee.
   * This is needed since the data aggregation service doesn't track the franchisee aggregate data belongs to, only the branch.
   * So instead we'll get the branch ids for a selected territory, or the branch id(s) for a given franchisee & branch combination.
   * @returns an array of ids.
   */
  protected getSelectedBranchIds(): string[] | undefined {
    return this.resolveSelectedBranchIds(this.selectedTerritoryId, this.selectedFranchiseeId, this.selectedBranchId);
  }

  /**
   * The one resolver behind every tab's branch filter. The overview tab and the delivery pipeline
   * tab hold their selections in separate properties but resolve them identically, so this takes
   * the three ids rather than existing twice.
   *
   * The return type carries meaning that the server depends on, so it is worth being explicit:
   *
   *  - `[]`        no branch filter was asked for. Show everything in scope. Only the
   *                "no franchisee selected" case may return this.
   *  - `undefined` a selection WAS made and resolved to no branches. Do not query at all.
   *  - ids         the branches to filter by.
   *
   * Never return `[]` from a path that resolved a selection. TaskGetPurchaseOrderList reads an
   * empty list as "no filter" and widens to the supplier's whole dealer network, so collapsing a
   * franchisee or territory that has no branches into `[]` shows more than was asked for, not less.
   * That is why the franchisee branch below tests `branches?.length` and not just `branches`:
   * getBranchesByFranchiseeId returns a real but empty array for a franchisee whose deployment has
   * no branches, and such a franchisee is still offered in the filter.
   */
  private resolveSelectedBranchIds(
    territoryId?: string,
    franchiseeId?: string,
    branchId?: string
  ): string[] | undefined {
    if (!isEmptyOrSpace(territoryId)) {
      const branchIds = this.territoryManager!.getBranchLinksByTerritory(territoryId!).map(l => l.branchId);
      return branchIds.length > 0 ? branchIds : undefined;
    }

    if (isEmptyOrSpace(franchiseeId)) {
      return [];
    }

    if (isEmptyOrSpace(branchId)) {
      const branches = this.dashboardFilterCache.getBranchesByFranchiseeId(franchiseeId);
      return branches?.length ? branches.map(b => b.id) : undefined;
    }

    return [branchId!];
  }

  protected onDateRangeChanged = (e: CustomEvent<{ date?: DateRange }>) => {
    const dateRange = e.detail.date;
    if (dateRange) {
      this.selectedDateRange = dateRange;
    }
  };

  protected onTerritoryChanged = (e: CustomEvent<{ territoryId?: string }>) => {
    const territoryId = e.detail.territoryId;
    if (territoryId) {
      this.selectedTerritoryId = territoryId;
      this.selectedFranchiseeId = emptyGuid;
      this.selectedBranchId = emptyGuid;
      this.selectedUserId = emptyGuid;
    }
  };

  protected onFranchiseeChanged = (e: CustomEvent<{ franchiseeId?: string }>) => {
    const franchiseeId = e.detail.franchiseeId;
    if (franchiseeId) {
      this.selectedTerritoryId = emptyGuid;
      this.selectedFranchiseeId = franchiseeId;
      this.selectedBranchId = emptyGuid;
      this.selectedUserId = emptyGuid;
    }
  };

  protected onBranchChanged = (e: CustomEvent<{ branchId?: string }>) => {
    const branchId = e.detail.branchId;
    if (branchId) {
      this.selectedBranchId = branchId;
      this.selectedUserId = emptyGuid;
    }
  };

  protected onUserChanged = (e: CustomEvent<{ userId?: string }>) => {
    if (e.detail.userId) {
      this.selectedUserId = e.detail.userId;
    }
  };

  protected onFilterReset = (_: CustomEvent) => {
    if (this.availableDateRanges.length > 1) this.selectedDateRange = this.availableDateRanges[1];
    this.selectedTerritoryId = emptyGuid;
    this.selectedFranchiseeId = emptyGuid;
    this.selectedBranchId = emptyGuid;
    this.selectedUserId = emptyGuid;
  };
  // The delivery pipeline tab keeps its own filter selections, so that changing the pipeline's
  // branch or date range does not disturb the overview tab and vice versa.
  protected onDateRangeChangedForPipeline = (e: CustomEvent<{ date?: DateRange }>) => {
    const dateRange = e.detail.date;
    if (dateRange) {
      this.selectedDateRangeForPipeline = dateRange;
    }
  };

  protected onTerritoryChangedForPipeline = (e: CustomEvent<{ territoryId?: string }>) => {
    const territoryId = e.detail.territoryId;
    if (territoryId) {
      this.selectedTerritoryIdForPipeline = territoryId;
      this.selectedFranchiseeIdForPipeline = emptyGuid;
      this.selectedBranchIdForPipeline = emptyGuid;
      this.selectedUserIdForPipeline = emptyGuid;
    }
  };

  protected onBranchChangedForPipeline = (e: CustomEvent<{ branchId?: string }>) => {
    const branchId = e.detail.branchId;
    if (branchId) {
      this.selectedBranchIdForPipeline = branchId;
      this.selectedUserIdForPipeline = emptyGuid;
    }
  };

  protected onUserChangedForPipeline = (e: CustomEvent<{ userId?: string }>) => {
    if (e.detail.userId) {
      this.selectedUserIdForPipeline = e.detail.userId;
    }
  };

  protected onFilterResetForPipeline = (_: CustomEvent<NonNullable<unknown>>) => {
    if (this.availableDateRanges.length > 1) {
      this.selectedDateRangeForPipeline = this.availableDateRanges[1];
    }
    this.selectedTerritoryIdForPipeline = emptyGuid;
    this.selectedFranchiseeIdForPipeline = emptyGuid;
    this.selectedBranchIdForPipeline = emptyGuid;
    this.selectedUserIdForPipeline = emptyGuid;
  };

  protected getSelectedBranchIdsForPipeline(): string[] | undefined {
    return this.resolveSelectedBranchIds(
      this.selectedTerritoryIdForPipeline,
      this.selectedFranchiseeIdForPipeline,
      this.selectedBranchIdForPipeline
    );
  }

  protected onFranchiseeChangedForPipeline = (e: CustomEvent<{ franchiseeId?: string }>) => {
    const franchiseeId = e.detail.franchiseeId;
    if (franchiseeId) {
      this.selectedFranchiseeIdForPipeline = franchiseeId;
      this.selectedBranchIdForPipeline = emptyGuid;
      this.selectedUserIdForPipeline = emptyGuid;
    }
  };
}
